import {isCampaignManufacturer, isCampaignMarketSearch} from "../../utils/Utils";

// Pure helpers for the "Marketplace Operator" campaign profile. The setting lives
// in campaign.settings.marketplace_operator and is read by pricerest; the 1P seller
// is the campaign's existing Seller Code, not part of this setting.

const MARKETPLACE_KEY_LENGTH = 15;

// Card identifiers used by the campaign type cards on the create/edit screen.
export const CARD_BUYBOX = 'buybox';
export const CARD_MARKETPLACE_OPERATOR = 'marketplace_operator';

// One trailing domain suffix (.com/.net/.org/.co, then .tr/.uk/.de) is not part of
// the marketplace: hepsiburada.com and HepsiBurada are the same marketplace.
const DOMAIN_SUFFIX = /(\.com|\.net|\.org|\.co)?(\.tr|\.uk|\.de)?$/i;

// JS twin of pricerest's normalize_marketplace_key: a leading "www." / "www_" is not
// part of the marketplace, nor is one trailing domain suffix (amazon.com.tr -> amazon),
// then lowercase ASCII letters only (as Mongo $toLower does), '.' -> '_', and the
// first 15 code points.
export const normalizeMarketplaceKey = (key) => {
    if (typeof key !== 'string') return '';
    const name = key.replace(/^www[._]/i, '').replace(DOMAIN_SUFFIX, '');
    const lowered = name.replace(/[A-Z]/g, (c) => c.toLowerCase()).replace(/\./g, '_');
    return Array.from(lowered).slice(0, MARKETPLACE_KEY_LENGTH).join('');
};

// Parses the competitor marketplaces text field ("trendyol, hepsiburada n11")
// into normalized, deduped keys in the order they were typed.
export const parseMarketplaceList = (text) => {
    if (typeof text !== 'string') return [];
    const keys = [];
    text.split(/[\s,]+/).forEach((part) => {
        const key = normalizeMarketplaceKey(part);
        if (key && !keys.includes(key)) keys.push(key);
    });
    return keys;
};

export const readMarketplaceOperator = (settings) => {
    const raw = settings?.marketplace_operator;
    return {
        enabled: !!raw?.enabled,
        own_marketplace_key: typeof raw?.own_marketplace_key === 'string' ? raw.own_marketplace_key : '',
        competitor_marketplaces: Array.isArray(raw?.competitor_marketplaces) ? raw.competitor_marketplaces : [],
    };
};

// Builds the object stored in settings: keys are trimmed, normalized and deduped.
export const toMarketplaceOperatorSetting = (form) => {
    const current = readMarketplaceOperator({marketplace_operator: form});
    const competitors = [];
    current.competitor_marketplaces.forEach((raw) => {
        const key = normalizeMarketplaceKey(typeof raw === 'string' ? raw.trim() : raw);
        if (key && !competitors.includes(key)) competitors.push(key);
    });
    return {
        enabled: current.enabled,
        own_marketplace_key: normalizeMarketplaceKey(current.own_marketplace_key.trim()),
        competitor_marketplaces: competitors,
    };
};

// The backend computes the marketplace fields only in the seller pipeline, so
// manufacturer and market-search campaigns never count as operator campaigns.
// Type badge for operator campaigns on the campaign list and the topbar
// selector; same icon/color as the operator card in CampaignEdit.js.
export const MARKETPLACE_OPERATOR_META = {icon: 'pi pi-building', color: '#0d9488', bg: '#ccfbf1'};

export const isMarketplaceOperatorCampaign = (campaign) => {
    const operator = readMarketplaceOperator(campaign?.settings);
    if (!operator.enabled || !operator.own_marketplace_key) return false;
    return !isCampaignManufacturer(campaign) && !isCampaignMarketSearch(campaign);
};

// Applies a campaign type card to a settings object. card is one of the four
// real usage types, 'buybox' or 'marketplace_operator'. Returns a new object.
export const applyCampaignCard = (settings, card) => {
    const next = {...(settings ?? {})};
    if (card === CARD_BUYBOX) {
        next.usage_type = 'seller';
        next.marketplace_api = {};
        delete next.marketplace_operator;
    } else if (card === CARD_MARKETPLACE_OPERATOR) {
        next.usage_type = 'seller';
        next.marketplace_operator = settings?.marketplace_operator?.enabled
            ? settings.marketplace_operator
            : {enabled: true, own_marketplace_key: '', competitor_marketplaces: []};
        delete next.marketplace_api;
    } else {
        next.usage_type = card;
        delete next.marketplace_api;
        delete next.marketplace_operator;
    }
    return next;
};

// Which card shows as selected. The synthetic cards win over usage_type, which
// stays 'seller' for Competitive, Buybox and Marketplace Operator alike.
export const activeCampaignCard = (settings) => {
    if (settings?.marketplace_operator?.enabled) return CARD_MARKETPLACE_OPERATOR;
    if (settings?.marketplace_api) return CARD_BUYBOX;
    return settings?.usage_type;
};
